import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { Contact, InsertContact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { Plus, Mail, Phone, Building, Pencil, Trash2, User } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { EmailComposerDialog } from "@/components/email-composer-dialog";

export default function ContactsPage() {
    const { hasPermission } = useAuth();
    const { toast } = useToast();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

    const { data: contacts, isLoading } = useQuery<Contact[]>({
        queryKey: ["/api/contacts"],
    });

    const canWrite = hasPermission("contacts", "write");

    const createMutation = useMutation({
        mutationFn: async (data: InsertContact) => {
            return await apiRequest("POST", "/api/contacts", data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
            toast({ title: "Contact created successfully" });
            setDialogOpen(false);
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to create contact",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<Contact> }) => {
            return await apiRequest("PATCH", `/api/contacts/${id}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
            toast({ title: "Contact updated successfully" });
            setDialogOpen(false);
            setEditingContact(null);
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to update contact",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            return await apiRequest("DELETE", `/api/contacts/${id}`, {});
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
            toast({ title: "Contact deleted successfully" });
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to delete contact",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const data = {
            firstName: formData.get("firstName") as string,
            lastName: formData.get("lastName") as string,
            email: formData.get("email") as string,
            phone: formData.get("phone") as string,
            company: formData.get("company") as string,
            position: formData.get("position") as string,
            status: formData.get("status") as "active" | "inactive" | "lead",
            notes: formData.get("notes") as string,
        };

        if (editingContact) {
            updateMutation.mutate({ id: editingContact.id, data });
        } else {
            createMutation.mutate(data as InsertContact);
        }
    };

    const getInitials = (firstName?: string, lastName?: string) => {
        const f = firstName ?? "";
        const l = lastName ?? "";
        const first = f.length > 0 ? f[0] : "";
        const last = l.length > 0 ? l[0] : "";
        return `${first}${last}`.toUpperCase();
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case "active":
                return "bg-chart-3/10 text-chart-3 border-chart-3/20";
            case "inactive":
                return "bg-muted/50 text-muted-foreground border-muted";
            case "lead":
                return "bg-chart-1/10 text-chart-1 border-chart-1/20";
            default:
                return "";
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Contacts</h1>
                    <p className="text-muted-foreground mt-2">Manage your customer and lead information</p>
                </div>
                {canWrite && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={() => setEditingContact(null)} data-testid="button-create-contact">
                                <Plus className="mr-2 h-4 w-4" />
                                Add Contact
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>{editingContact ? "Edit Contact" : "Create New Contact"}</DialogTitle>
                                <DialogDescription>
                                    {editingContact ? "Update contact information" : "Add a new contact to your CRM"}
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="firstName">First Name *</Label>
                                        <Input id="firstName" name="firstName" defaultValue={editingContact?.firstName} required data-testid="input-first-name" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lastName">Last Name *</Label>
                                        <Input id="lastName" name="lastName" defaultValue={editingContact?.lastName} required data-testid="input-last-name" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input id="email" name="email" type="email" defaultValue={editingContact?.email || ""} data-testid="input-contact-email" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone</Label>
                                    <Input id="phone" name="phone" defaultValue={editingContact?.phone || ""} data-testid="input-phone" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="company">Company</Label>
                                        <Input id="company" name="company" defaultValue={editingContact?.company || ""} data-testid="input-company" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="position">Position</Label>
                                        <Input id="position" name="position" defaultValue={editingContact?.position || ""} data-testid="input-position" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="status">Status *</Label>
                                    <Select name="status" defaultValue={editingContact?.status || "lead"} required>
                                        <SelectTrigger data-testid="select-status">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="lead">Lead</SelectItem>
                                            <SelectItem value="active">Active</SelectItem>
                                            <SelectItem value="inactive">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="notes">Notes</Label>
                                    <Textarea id="notes" name="notes" defaultValue={editingContact?.notes || ""} rows={3} data-testid="input-notes" />
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingContact(null); }}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-submit-contact">
                                        {editingContact ? "Update" : "Create"} Contact
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <Card>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-6 space-y-4">{[1, 2, 3].map((i) => (<Skeleton key={i} className="h-16 w-full" />))}</div>
                    ) : contacts && contacts.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Contact</TableHead>
                                    <TableHead>Company</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>Status</TableHead>
                                    {canWrite && <TableHead className="text-right">Actions</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {contacts.map((contact) => (
                                    <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Avatar className="w-10 h-10">
                                                    <AvatarFallback className="bg-primary/10 text-primary">{getInitials(contact.firstName, contact.lastName)}</AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="font-medium">{contact.firstName} {contact.lastName}</p>
                                                    {contact.position && (<p className="text-sm text-muted-foreground">{contact.position}</p>)}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {contact.company && (
                                                <div className="flex items-center gap-2">
                                                    <Building className="w-4 h-4 text-muted-foreground" />
                                                    <span>{contact.company}</span>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {contact.email && (
                                                <div className="flex items-center gap-2">
                                                    <Mail className="w-4 h-4 text-muted-foreground" />
                                                    <span className="text-sm">{contact.email}</span>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {contact.phone && (
                                                <div className="flex items-center gap-2">
                                                    <Phone className="w-4 h-4 text-muted-foreground" />
                                                    <span className="text-sm">{contact.phone}</span>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={getStatusColor(contact.status)}>{contact.status}</Badge>
                                        </TableCell>
                                        {canWrite && (
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    {contact.email && (
                                                        <Button variant="ghost" size="icon" onClick={() => { setSelectedContact(contact); setEmailDialogOpen(true); }} data-testid={`button-email-${contact.id}`}>
                                                            <Mail className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button variant="ghost" size="icon" onClick={() => { setEditingContact(contact); setDialogOpen(true); }} data-testid={`button-edit-${contact.id}`}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => { if (confirm("Are you sure you want to delete this contact?")) { deleteMutation.mutate(contact.id); } }} data-testid={`button-delete-${contact.id}`}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <User className="h-12 w-12 text-muted-foreground/50 mb-4" />
                            <h3 className="text-lg font-semibold">No contacts yet</h3>
                            <p className="text-sm text-muted-foreground mt-2 max-w-sm">Get started by adding your first contact to begin managing relationships.</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            <EmailComposerDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen} recipientEmail={selectedContact?.email || ""} recipientName={`${selectedContact?.firstName || ""} ${selectedContact?.lastName || ""}`.trim()} contactId={selectedContact?.id} />
        </div>
    );
}
